<template>
    <div class="chartContainer">
        <div v-if="isLoading" class="loading-pie">
            <content-loader height="250" />
        </div>
        <apexchart
            v-if="!isLoading && isMounted"
            :height="height"
            :options="computedChartOptions"
            :series="series"
            :width="width"
            @data-point-selection="onPieChartClick"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ContentLoader } from 'vue-content-loader';

export default defineComponent({
    name: 'PieChart',
    components: { ContentLoader },
    emits: ['dataPointClicked'],
    computed: {
        series(): string[] {
            return this.seriesValues;
        },
        computedChartOptions(): Record<string, unknown> {
            const base = this.chartOptions as Record<string, unknown>;
            return {
                ...base,
                labels: this.labels,
                colors: this.colors,
            };
        },
    },
    data() {
        return {
            isMounted: false,
            chartOptions: {
                chart: {
                    type: 'donut',
                    id: `piechart-${Math.random().toString(36).substr(2, 9)}`,
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '65%', // Valeur par défaut: 65%. Plus la valeur est petite, plus le donut est épais
                        },
                    },
                },
                legend: {
                    show: this.showLegend,
                    fontWeight: 400,
                    fontFamily: 'Roboto, sans-serif',
                    position: 'right',
                },
                colors: this.colors,
                dataLabels: {
                    enabled: this.showDataLabels,
                    formatter: (val: number, opts: any) => {
                        return this.isPercentage ? `${val.toFixed(1)} %` : opts.w.config.series[opts.seriesIndex];
                    },
                    style: {
                        fontSize: '14px',
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 500,
                    },
                },
                labels: this.labels,
                responsive: [
                    {
                        breakpoint: 480,
                        options: {
                            chart: {
                                width: 200,
                            },
                            legend: {
                                position: 'bottom',
                            },
                        },
                    },
                ],
                title: {
                    text: this.title,
                    align: 'center',
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: 'Roboto, sans-serif',
                    },
                },
                states: {
                    hover: {
                        filter: {
                            type: 'opacity',
                            value: 0.8,
                        },
                    },
                },
            },
        };
    },
    mounted() {
        this.isMounted = true;
    },
    methods: {
        onPieChartClick(_event: any, _chartContext: any, config: any) {
            this.$emit('dataPointClicked', config.dataPointIndex);
        },
    },
    props: {
        title: String,
        seriesValues: { type: Array as PropType<string[]>, required: true },
        colors: { type: Array as PropType<string[]>, required: true },
        labels: { type: Array as PropType<string[]>, required: true },
        isLoading: { type: Boolean, required: true },
        isPercentage: Boolean,
        showLegend: Boolean,
        showDataLabels: Boolean,
        height: { type: String, required: false, default: '95%' },
        width: { type: String, required: false, default: '450px' },
    },
});
</script>

<style scoped>
.chartContainer {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
}
.loading-pie {
    height: 150px;
    width: 150px;
    border-radius: 50%;
    overflow: hidden;
}
@media screen and (min-width: 980px) {
    .chartContainer {
        align-self: center;
    }
}
@media screen and (min-width: 1280px) and (min-height: 900px) {
    .chartContainer {
        height: 250px;
    }
    .loading-pie {
        height: 250px;
        width: 250px;
    }
}
</style>
